/**
 * Frontend analytics client. Batches events into a 5s flush window so we
 * don't hammer the API. Failures swallowed — analytics never breaks the UX.
 *
 *   emit('session_start')
 *   emit('draft_approved', { actionId })
 *   emit('integration_connected', { provider: 'gmail' })
 */
import { api } from './api';

interface Pending {
  name: string;
  props?: Record<string, unknown>;
  at: string;
}

const queue: Pending[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flushNow, 5_000);
}

async function flushNow() {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await api('/v1/events', { method: 'POST', body: JSON.stringify({ events: batch }) });
  } catch {
    // Don't requeue — analytics is best-effort. Drop on failure.
  }
}

export function emit(name: string, props?: Record<string, unknown>): void {
  queue.push({ name, props, at: new Date().toISOString() });
  if (queue.length >= 10) flushNow();
  else scheduleFlush();
}

// Flush on tab close so we don't lose the last batch.
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && queue.length > 0) {
      // Best-effort sendBeacon-ish: just flush synchronously.
      flushNow();
    }
  });
}
